import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { DashboardWeeklySupplierPerformance } from '../../../api/dashboardApi';
import {
    formatNumber,
    generateWeekDates,
    getWeekdayOnly,
    getSlotTooltipItems,
} from '@/features/dashboard/utils/dashboardUtils';

interface WeeklyPerformanceTableProps {
    supplierPerformances: DashboardWeeklySupplierPerformance[];
    weekStartDate: string;
    weekEndDate: string;
    displayMode: 'count' | 'rate';
}

const parseRate = (rateStr: string) => parseInt(rateStr?.replace('%', '')) || 0;

const WeeklyPerformanceTable: React.FC<WeeklyPerformanceTableProps> = ({
                                                                           supplierPerformances,
                                                                           weekStartDate,
                                                                           weekEndDate,
                                                                           displayMode,
                                                                       }) => {
    const [hoveredCell, setHoveredCell] = useState<{ suppId: string; date: string } | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const dates = generateWeekDates(weekStartDate, weekEndDate);

    return (
        <>
            <table className="table dashboard-weekly-table supplier-performance-table">
                <thead>
                <tr>
                    <th className="col-partner-name">협력사명</th>
                    <th className="col-slot-set">세트</th>
                    <th className="col-completed">완료 건수</th>
                    <th className="col-accept-rate">수락률</th>
                    {dates.map((date) => (
                        <th key={date} className="col-day">
                            {getWeekdayOnly(date)}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {supplierPerformances.length > 0 ? (
                    supplierPerformances.map((supplier, index) => (
                        <tr key={supplier.suppId} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                            <td className="col-partner-name">{supplier.suppName}</td>
                            <td className="col-slot-set">{supplier.slotSetCnt ?? '-'}</td>
                            <td className="col-completed">{formatNumber(supplier.finishCnt)}</td>
                            <td className="col-accept-rate">
                                {(supplier.acceptanceRate ?? 0).toFixed(1)}%
                            </td>
                            {dates.map((date) => {
                                const slots = supplier.dailySlots?.[date];
                                const slotCounts = supplier.dailySlotCounts?.[date];

                                let dayRate = 0;
                                const missionRate = supplier.dailyMissionRate?.[date];
                                if (missionRate !== undefined) {
                                    dayRate = parseRate(missionRate);
                                } else if (slots && slots.length > 0) {
                                    // target=0 슬롯은 '%' 없는 숫자 문자열로 오므로 '%' 포함 여부로 유효 슬롯 판별
                                    const validSlots = slots.filter((s: string) => s.includes('%'));
                                    const achievedCount = validSlots.filter(
                                        (s: string) => parseRate(s) >= 100,
                                    ).length;
                                    dayRate =
                                        validSlots.length > 0
                                            ? (achievedCount / validSlots.length) * 100
                                            : 0;
                                }

                                let totalCount = 0;
                                let totalTarget = 0;
                                if (slotCounts && slotCounts.length > 0) {
                                    slotCounts.forEach((countStr: string) => {
                                        if (countStr === '-') return;
                                        const [count, target] = countStr.split('/').map(Number);
                                        totalCount += count || 0;
                                        totalTarget += target || 0;
                                    });
                                }

                                return (
                                    <td
                                        key={date}
                                        className={`col-day-cell ${dayRate >= 100 ? 'achieved' : dayRate > 0 ? 'partial' : ''}`}
                                        onMouseEnter={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setTooltipPosition({
                                                top: rect.top - 10,
                                                left: rect.left + rect.width / 2,
                                            });
                                            setHoveredCell({ suppId: supplier.suppId, date });
                                        }}
                                        onMouseLeave={() => {
                                            setHoveredCell(null);
                                            setTooltipPosition(null);
                                        }}
                                    >
                                        {displayMode === 'rate'
                                            ? `${dayRate.toFixed(0)}%`
                                            : `${totalCount}/${totalTarget}`}
                                    </td>
                                );
                            })}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={10} className="empty-message">
                            협력사별 수행 현황 데이터가 없습니다.
                        </td>
                    </tr>
                )}
                </tbody>
            </table>

            {hoveredCell &&
                tooltipPosition &&
                ReactDOM.createPortal(
                    <div
                        ref={tooltipRef}
                        className="slot-tooltip-portal"
                        style={{
                            position: 'fixed',
                            top: tooltipPosition.top,
                            left: tooltipPosition.left,
                            transform: 'translate(-50%, -100%)',
                            zIndex: 99999,
                        }}
                    >
                        {(() => {
                            const hoveredSupplier = supplierPerformances.find(
                                (s) => s.suppId === hoveredCell.suppId,
                            );
                            const tooltipItems = hoveredSupplier
                                ? getSlotTooltipItems(hoveredSupplier, hoveredCell.date)
                                : null;
                            if (!tooltipItems)
                                return <div className="tooltip-no-data">데이터 없음</div>;
                            return (
                                <>
                                    {tooltipItems.map(
                                        (slot: {
                                            label: string;
                                            isNa: boolean;
                                            isAchieved: boolean;
                                            isNoTarget: boolean;
                                            count: string;
                                            rate: string;
                                        }) => (
                                            <div
                                                key={slot.label}
                                                className={`tooltip-item ${slot.isNa ? 'slot-na-item' : slot.isNoTarget ? 'no-target' : slot.isAchieved ? 'achieved' : 'not-achieved'}`}
                                            >
                                                {slot.label}:{' '}
                                                {slot.isNa
                                                    ? '비해당'
                                                    : slot.isNoTarget
                                                        ? `${slot.count}건`
                                                        : `${slot.count} (${slot.rate})`}
                                            </div>
                                        ),
                                    )}
                                </>
                            );
                        })()}
                    </div>,
                    document.body,
                )}
        </>
    );
};

export default WeeklyPerformanceTable;
